import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useTimer } from "react-timer-hook";
import "./otp.css";
import { useNavigate } from "react-router";
import { getReturnUrl } from "../../utils/auth";

const OTP = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [otpResend, setOtpResend] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const inputRefs = useRef([]);
  const urlParams = new URLSearchParams(window.location.search);
  const emailFromUrl = urlParams.get("email") || "";
  const role = urlParams.get("role") || "";
  const from = urlParams.get("from") || "signup";

  const VERIFY_OTP_ENDPOINT = "/wp-json/dbsb/v1/verify-otp";
  const RESEND_OTP_ENDPOINT = "/wp-json/dbsb/v1/resend-otp";
  const USER_ME_ENDPOINT = "/wp-json/dbsb/v1/user/me";

  const expiryTimestamp = new Date();
  expiryTimestamp.setSeconds(expiryTimestamp.getSeconds() + 60);

  const { seconds, restart } = useTimer({
    expiryTimestamp,
    onExpire: () => setOtpResend(true),
  });

  // Function to fetch user email from /user/me endpoint
  const fetchUserEmail = async () => {
    setIsLoadingEmail(true);
    setError("");
    
    try {
      // Check if we have the nonce for authentication
      if (!window.wpApiSettings?.nonce) {
        throw new Error("Authentication token not found. Please try logging in again.");
      }

      const response = await fetch(USER_ME_ENDPOINT, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
        credentials: "same-origin",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch user data (${response.status})`);
      }

      const userData = await response.json();
      console.log("User data fetched:", userData); // Debug log
      
      if (userData.email) {
        setUserEmail(userData.email);
        console.log("Email found:", userData.email); // Debug log
        // Auto-send OTP when we get the email
        await resendOTP(userData.email);
      } else {
        throw new Error("No email found for user");
      }
    } catch (error) {
      console.error("Error fetching user email:", error); // Debug log
      setError(`Unable to fetch your email: ${error.message}`);
    } finally {
      setIsLoadingEmail(false);
    }
  };

  // Function to resend OTP (extracted from handleResendOTP for reuse)
  const resendOTP = async (emailToUse) => {
    setIsResending(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(RESEND_OTP_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: emailToUse }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to resend OTP");
      }

      setSuccess("New OTP sent successfully!");
      setOtpResend(false);
      const newExpiryTimestamp = new Date();
      newExpiryTimestamp.setSeconds(newExpiryTimestamp.getSeconds() + 60);
      restart(newExpiryTimestamp);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsResending(false);
    }
  };

  const handleResendOTP = async () => {
    if (otpResend) {
      const emailToUse = emailFromUrl || userEmail;
      if (!emailToUse) {
        setError("No email available. Please try logging in again.");
        return;
      }
      await resendOTP(emailToUse);
    }
  };

  const handleChange = (index, value) => {
    const newCode = [...code];
    // Only handle single-character input here
    newCode[index] = value.slice(0, 1); // Ensure only one character is set
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    setError("");
    setSuccess("");
  };

  const handlePaste = (index, e) => {
    e.preventDefault(); // Prevent default paste behavior
    const pastedText = e.clipboardData.getData("text").trim();
    if (pastedText.length === 6 && /^\d+$/.test(pastedText)) {
      // If the pasted text is exactly 6 digits, distribute across all inputs
      const newCode = pastedText.split("");
      setCode(newCode);
      // Focus the last input after pasting
      inputRefs.current[5]?.focus();
      setError("");
      setSuccess("");
    } else {
      // If the pasted text is invalid, show an error
      setError("Please paste a valid 6-digit code");
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const verificationCode = code.join("");
    if (verificationCode.length !== 6) {
      setError("Please enter a 6-digit code");
      return;
    }

    setIsVerifying(true);
    setError("");
    setSuccess("");

    try {
      const emailToUse = emailFromUrl || userEmail;
      if (!emailToUse) {
        setError("No email available. Please try logging in again.");
        return;
      }

      const response = await fetch(VERIFY_OTP_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: emailToUse,
          otp: verificationCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "OTP verification failed");
      }

      setSuccess("OTP verified successfully!");
      setCode(["", "", "", "", "", ""]);
      
      // Check for return URL in query params
      const returnUrl = getReturnUrl(window.location.search);
      
      // Determine next page based on role and return URL
      let nextPage;
      if (returnUrl) {
        nextPage = returnUrl;
      } else if (role === "healthProvider" && urlParams.get("next") === "verify") {
        nextPage = "/verify";
      } else {
        nextPage = "/dashboard";
      }
      
      window.location.href = nextPage;
    } catch (error) {
      setError(error.message);
    } finally {
      setIsVerifying(false);
    }
  };

  // Fetch user email if not provided in URL
  useEffect(() => {
    console.log("OTP useEffect triggered, emailFromUrl:", emailFromUrl); // Debug log
    if (!emailFromUrl) {
      console.log("No email in URL, fetching user email..."); // Debug log
      fetchUserEmail();
    } else {
      console.log("Email found in URL:", emailFromUrl); // Debug log
    }
  }, []);

  useEffect(() => {
    if (code.every((digit) => digit !== "")) {
      handleSubmit(new Event("submit"));
    }
  }, [code]);

  const containerVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const formVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  return (
    <motion.div
      className="otp-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="otp-card" variants={containerVariants}>
        <div className="logo">
          <a href="/">
            <img
              src={`${process.env.PUBLIC_URL}/assets/logo.svg`}
              alt="Direct by Syndicate Bio"
            />
          </a>
        </div>
        <motion.div
          className="otp-content"
          variants={formVariants}
          initial="hidden"
          animate="visible"
        >
          <h2>Check your email for a code</h2>
          {isLoadingEmail ? (
            <p>
              Loading your email address...
            </p>
          ) : (
            <p>
              We sent a 6-digit code to <span>{emailFromUrl || userEmail || "your email"}</span> to
              verify your email.
            </p>
          )}
          {otpResend ? (
            <motion.a
              className={`resend-link ${isResending || isLoadingEmail ? "disabled" : ""}`}
              onClick={handleResendOTP}
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.1 }}
            >
              {isResending ? "Resending..." : isLoadingEmail ? "Loading..." : "Resend code"}
            </motion.a>
          ) : (
            <p className="resend-timer">
              Request new code in 00:{seconds < 10 ? `0${seconds}` : seconds}
            </p>
          )}
          <motion.form
            onSubmit={handleSubmit}
            className="otp-inputs"
            variants={itemVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="otp-boxes">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onPaste={(e) => handlePaste(index, e)} // Add onPaste handler
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="otp-input"
                  disabled={isVerifying || isLoadingEmail}
                />
              ))}
            </div>
            {error && <span className="error">{error}</span>}
            {success && <span className="success-message">{success}</span>}
            <motion.button
              type="submit"
              className={`create-account-button ${
                isVerifying || isLoadingEmail || code.some((digit) => !digit) ? "disabled" : ""
              }`}
              variants={itemVariants}
              whileHover={{ scale: 1.02, backgroundColor: "#d1d9e6" }}
              whileTap={{ scale: 0.98 }}
              disabled={isVerifying || isLoadingEmail || code.some((digit) => !digit)}
            >
              {isVerifying ? "Verifying..." : isLoadingEmail ? "Loading..." : "Create account"}
            </motion.button>
          </motion.form>
          <div className="email-links">
            <button
              className="email-button gmail"
              onClick={() => window.open("https://mail.google.com", "_blank")}
            >
              <img
                src={`${process.env.PUBLIC_URL}/assets/gmail.png`}
                alt="Gmail"
              />
              Open Gmail
            </button>
            <button
              className="email-button outlook"
              onClick={() => window.open("https://outlook.live.com", "_blank")}
            >
              <img
                src={`${process.env.PUBLIC_URL}/assets/outlook.png`}
                alt="Outlook"
              />
              Open Outlook
            </button>
          </div>
          <div className="or-line">
            <ul></ul>
            <p className="login-link">OR</p>
            <ul></ul>
          </div>
          <motion.a
            href={
              from === "login"
                ? "/login?fromOtp=true"
                : `/register?role=${role}&fromOtp=true`
            }
            onClick={(e) => {
              e.preventDefault();
              if (from === "login") {
                navigate("/login?fromOtp=true");
              } else {
                navigate(`/register?role=${role}&fromOtp=true`);
              }
            }}
            className="change-email"
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.3 }}
          >
            Change email
          </motion.a>
        </motion.div>
      </motion.div>
      <motion.p
        className="spam-tip"
        variants={itemVariants}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.4 }}
      >
        Can’t find email? Check your spam folder
      </motion.p>
    </motion.div>
  );
};

export default OTP;
